/**
 * Single Source of Truth for all plan configurations
 * This file contains all plan data: features, limits, pricing, and Stripe IDs
 *
 * For dynamic plan data, use useFreeAccess().plans from FreeAccessContext.
 * The static PLANS export below defaults to freeAccessMode=false with
 * hardcoded limits, and is only safe for module-level code that accesses
 * paid plan Stripe IDs/prices (e.g. stripe.ts).
 */

import {
  FREE_ACCESS_LIMITS_DEFAULTS,
  buildFreeAccessFeatures,
  type FreeAccessLimits,
} from "./free-access";

export type PlanKey = "free" | "growth" | "pro" | "agency";
export type BillingInterval = "monthly" | "annual";

/** Numeric limits for a single plan tier. */
export interface PlanLimitsConfig {
  max_stores: number;
  max_products: number;
  ai_optimizations_monthly: number;
  ai_chat_messages_monthly: number;
  max_bulk_operations_monthly: number;
}

/** Dynamic limits for every plan tier, loaded from platform_settings. */
export interface AllPlanLimits {
  free: PlanLimitsConfig;
  growth: PlanLimitsConfig;
  pro: PlanLimitsConfig;
  agency: PlanLimitsConfig;
}

/** Hardcoded defaults — used when DB settings are not yet available. */
export const DEFAULT_PLAN_LIMITS: AllPlanLimits = {
  free: {
    max_stores: 1,
    max_products: 50,
    ai_optimizations_monthly: 10,
    ai_chat_messages_monthly: 20,
    max_bulk_operations_monthly: 2,
  },
  growth: {
    max_stores: 1,
    max_products: 1000,
    ai_optimizations_monthly: 500,
    ai_chat_messages_monthly: 1000,
    max_bulk_operations_monthly: 50,
  },
  pro: {
    max_stores: 3,
    max_products: 5000,
    ai_optimizations_monthly: -1,
    ai_chat_messages_monthly: -1,
    max_bulk_operations_monthly: -1,
  },
  agency: {
    max_stores: 25,
    max_products: -1,
    ai_optimizations_monthly: -1,
    ai_chat_messages_monthly: -1,
    max_bulk_operations_monthly: -1,
  },
};

export interface PlanConfig {
  key: PlanKey;
  name: string;
  price: {
    monthly: number;
    annual: number;
  };
  period: string;
  description: string;
  bestFor: string;
  popular?: boolean;
  badge?: string;

  // Stripe configuration
  stripe?: {
    monthlyPriceId: string;
    annualPriceId: string;
    monthlyProductId: string;
    annualProductId: string;
  };

  // Plan limits
  limits: PlanLimitsConfig;

  // Features displayed in UI
  features: string[];

  // UI configuration
  ui: {
    color: string; // Tailwind classes for badge colors
    ctaText: string; // Default CTA text for non-authenticated users
    ctaHref: string; // Default CTA link for non-authenticated users
  };
}

// Annual savings percentage
export const ANNUAL_DISCOUNT_PERCENT = 30;

// ── Feature formatting helpers ──────────────────────────────────────────

function formatStoreFeature(n: number): string {
  if (n === 1) return "1 store";
  if (n === -1) return "Unlimited stores";
  return `Up to ${n} stores`;
}

function formatProductFeature(n: number): string {
  if (n === -1) return "Unlimited products";
  return `Up to ${n.toLocaleString()} products`;
}

function formatAiOptFeature(n: number): string {
  if (n === -1) return "Unlimited AI optimizations";
  return `${n.toLocaleString()} AI optimizations/month`;
}

function formatAiChatFeature(n: number): string {
  if (n === -1) return "Unlimited AI chat messages";
  return `${n.toLocaleString()} AI chat messages/month`;
}

/** Build the dynamic portion of a feature list from numeric limits. */
function buildLimitFeatures(limits: PlanLimitsConfig): string[] {
  return [
    formatStoreFeature(limits.max_stores),
    formatProductFeature(limits.max_products),
    formatAiOptFeature(limits.ai_optimizations_monthly),
    formatAiChatFeature(limits.ai_chat_messages_monthly),
  ];
}

/**
 * Build plan configurations based on free access mode state and dynamic limits.
 * Called dynamically by FreeAccessContext and statically for the default PLANS export.
 *
 * @param freeAccessMode  - whether free access is enabled
 * @param freeAccessLimits - dynamic limits for the free-access plan (from DB)
 * @param planLimits       - dynamic limits for every plan tier (from DB)
 */
export function buildPlans(
  freeAccessMode: boolean,
  freeAccessLimits: FreeAccessLimits = FREE_ACCESS_LIMITS_DEFAULTS,
  planLimits: AllPlanLimits = DEFAULT_PLAN_LIMITS,
): Record<PlanKey, PlanConfig> {
  const gl = planLimits.growth;
  const pl = planLimits.pro;
  const al = planLimits.agency;

  return {
    free: {
      key: "free",
      name: freeAccessMode ? "Free Access" : "Free",
      price: {
        monthly: 0,
        annual: 0,
      },
      period: freeAccessMode ? " — free during early access" : "/month forever",
      description: freeAccessMode ? "Full access during early access" : "Perfect for testing",
      bestFor: freeAccessMode
        ? "everyone — all features unlocked during early access"
        : "solo sellers validating a new store",

      limits: freeAccessMode
        ? { ...freeAccessLimits }
        : { ...planLimits.free },

      features: freeAccessMode
        ? buildFreeAccessFeatures(freeAccessLimits)
        : [
            ...buildLimitFeatures(planLimits.free),
            "SEO scoring & insights",
            "Analytics dashboard",
            "Community support",
          ],

      ui: {
        color: freeAccessMode ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
        ctaText: "Get Started Free",
        ctaHref: "/signup",
      },
    },

    growth: {
      key: "growth",
      name: "Growth",
      price: {
        monthly: 19,
        annual: 159.60, // 19 * 12 * 0.7 = €159.60/year
      },
      period: "/month",
      description: "For growing stores",
      bestFor: "stores ready for daily SEO wins and revenue insights",
      popular: true,
      badge: "Most Popular",

      stripe: {
        monthlyPriceId: "price_1SpZgcHqcbjy96SN8tLFQgyA",
        annualPriceId: "price_1SmZX9Hqcbjy96SN6uwdYL7T",
        monthlyProductId: "prod_ThNNTjwl8qQegj",
        annualProductId: "prod_Tk3e6kQPhxrXyv",
      },

      limits: { ...gl },

      features: [
        ...buildLimitFeatures(gl),
        "SEO scoring & insights",
        "Analytics dashboard",
        "100 cart recovery emails/month",
        "Priority email support",
      ],

      ui: {
        color: "bg-primary/10 text-primary",
        ctaText: "Get Started",
        ctaHref: "/signup",
      },
    },

    pro: {
      key: "pro",
      name: "Pro",
      price: {
        monthly: 49,
        annual: 411.60, // 49 * 12 * 0.7 = €411.60/year
      },
      period: "/month",
      description: "For power users",
      bestFor: "multi-store teams scaling insights and operations",

      stripe: {
        monthlyPriceId: "price_1SpZthHqcbjy96SNklYxGk8t",
        annualPriceId: "price_1SmZYAHqcbjy96SNZfspR5Nt",
        monthlyProductId: "prod_ThqDk9kAiDJdBH",
        annualProductId: "prod_Tk3fzg3VRh0dgt",
      },

      limits: { ...pl },

      features: [
        ...buildLimitFeatures(pl),
        "500 cart recovery emails/month",
        "Everything in Growth",
        "Priority support (email + phone)",
      ],

      ui: {
        color: "bg-blue-500/10 text-blue-600",
        ctaText: "Get Started",
        ctaHref: "/signup",
      },
    },

    agency: {
      key: "agency",
      name: "Agency",
      price: {
        monthly: 129,
        annual: 1083.60, // 129 * 12 * 0.7 = €1,083.60/year
      },
      period: "/month",
      description: "For agencies",
      bestFor: "agencies managing multiple client stores at scale",

      stripe: {
        monthlyPriceId: "price_1SkQcuHqcbjy96SNFDlP2UBy",
        annualPriceId: "price_1SmZYKHqcbjy96SN0wvPu5hq",
        monthlyProductId: "prod_ThNOVKXHnHSEVN",
        annualProductId: "prod_Tk3fhFYcnNTE3H",
      },

      limits: { ...al },

      features: [
        ...buildLimitFeatures(al),
        "1,000 cart recovery emails/month",
        "Everything in Pro",
        "Priority support (email + phone)",
      ],

      ui: {
        color: "bg-amber-500/10 text-amber-600",
        ctaText: "Get Started",
        ctaHref: "/signup",
      },
    },
  };
}

/**
 * Static plan configurations (default: free access mode OFF).
 * Used by module-level code (stripe.ts) that only accesses paid plans.
 * For dynamic access including the free plan, use useFreeAccess().plans instead.
 */
export const PLANS: Record<PlanKey, PlanConfig> = buildPlans(false);

// Helper functions for easy access
export const getPlan = (key: PlanKey): PlanConfig => PLANS[key];

export const getPaidPlans = (): PlanConfig[] =>
  Object.values(PLANS).filter(plan => plan.stripe);

export const getPlanByStripeProduct = (productId: string): PlanConfig | null => {
  return Object.values(PLANS).find(plan =>
    plan.stripe?.monthlyProductId === productId ||
    plan.stripe?.annualProductId === productId
  ) || null;
};

export const getPlanHierarchy = (): PlanKey[] => ["free", "growth", "pro", "agency"];

export const isUnlimited = (value: number): boolean => value === -1;

export const formatLimit = (value: number): string =>
  isUnlimited(value) ? "∞" : value.toLocaleString();

/**
 * Get the price ID for a plan based on billing interval
 */
export const getPriceId = (planKey: PlanKey, interval: BillingInterval): string | null => {
  const plan = PLANS[planKey];
  if (!plan.stripe) return null;
  return interval === "monthly" ? plan.stripe.monthlyPriceId : plan.stripe.annualPriceId;
};

/**
 * Get display price for a plan based on billing interval
 */
export const getDisplayPrice = (planKey: PlanKey, interval: BillingInterval): number => {
  const plan = PLANS[planKey];
  if (interval === "monthly") {
    return plan.price.monthly;
  }
  // For annual, show the equivalent monthly price
  return Math.round((plan.price.annual / 12) * 100) / 100;
};

/**
 * Expands "Everything in X" features to show actual inherited features
 */
export const getExpandedFeatures = (planKey: PlanKey): string[] => {
  const plan = PLANS[planKey];
  const expandedFeatures: string[] = [];
  const seen = new Set<string>();

  for (const feature of plan.features) {
    // Check if this is an "Everything in X" reference
    const match = feature.match(/^Everything in (Growth|Pro)$/i);
    if (match) {
      const inheritedPlanKey = match[1].toLowerCase() as "growth" | "pro";
      // Recursively get features from the inherited plan
      const inheritedFeatures = getExpandedFeatures(inheritedPlanKey);
      for (const inheritedFeature of inheritedFeatures) {
        if (!seen.has(inheritedFeature)) {
          seen.add(inheritedFeature);
          expandedFeatures.push(inheritedFeature);
        }
      }
    } else {
      if (!seen.has(feature)) {
        seen.add(feature);
        expandedFeatures.push(feature);
      }
    }
  }

  return expandedFeatures;
};
